// @flow

import {createSelector} from "reselect"
import {uniqBy, findIndex, flatMap} from "lodash"
import fuzzysort from "fuzzysort"

import type {
  BabyListItem,
  BabyListOptionalItem,
  ProductsState,
  ProductsByCategory,
  ProductOrSet,
  ProductSet,
  Product,
  ProductCategory,
  State,
  Action,
} from "../types"

const initialState: ProductsState = {
  entities: {
    products: [],
    media: [],
  },
  toggleds: [],
  revealeds: [],
}

const products = (state: ProductsState = initialState, action: Action) => {
  switch (action.type) {
    case "SET_PRODUCTS":
      return {
        ...state,
        entities: {
          ...state.entities,
          products: action.products,
        },
        toggleds: [],
        revealeds: [],
      }
    case "TOGGLE_REVEALED_PRODUCT":
      return {
        ...state,
        revealeds: [...state.revealeds, action.product],
      }
    case "TOGGLE_PRODUCT":
      return {
        ...state,
        toggleds: processToggling(state.toggleds, action.product),
      }
    case "RESET_TOGGLE_PRODUCT":
      return {
        ...state,
        toggleds: [],
      }
    case "SET_MEDIA":
      return {
        ...state,
        entities: {
          ...state.entities,
          media: action.media,
        },
      }
    case "CLEAR_MEDIA":
      return {
        ...state,
        entities: {
          ...state.entities,
          media: [],
        },
      }
    default:
      return state
  }
}

const processToggling = (
  toggleds: $ReadOnlyArray<ProductOrSet>,
  product
): $ReadOnlyArray<ProductOrSet> => {
  const index = getToggledProductIndex(toggleds, product)

  // is set
  if (product.isSet) {
    // and on list
    if (index > -1) {
      // -> close all
      return []
    }
    // -> add set, close rest
    return [product]
  }

  // not set, there can only be 1 toggled set
  const indexSet = getToggledProductSet(toggleds)

  // is product and on the list is the set that is parent of product
  if (index == -1 && indexSet > -1) {
    if (
      toggleds[indexSet].isSet &&
      toggleds[indexSet].products
        .map((psp) => psp.product.id)
        .find((id) => id == product.id)
    ) {
      // -> add on list
      return [toggleds[indexSet], product]
    }
  }

  // is product and on list, and on list is set that is parent of product
  if (index > -1 && indexSet > -1) {
    // -> remove from list
    return [...toggleds.slice(0, index), ...toggleds.slice(index + 1)]
  }

  if (index > -1) {
    // -> remove from list
    return [...toggleds.slice(0, index), ...toggleds.slice(index + 1)]
  }

  // close previous toggledProducts
  // -> add on list
  return [product]
}

const getToggledProductSet = (toggleds: $ReadOnlyArray<ProductOrSet>): number =>
  findIndex(toggleds, (item) => item.isSet)

const getToggledProductIndex = (
  toggleds: $ReadOnlyArray<ProductOrSet>,
  product: ProductOrSet
): number =>
  findIndex(
    toggleds,
    (item) => item.isSet == product.isSet && item.id == product.id
    // &&
    // item.productSetId == (!product.isSet ? product.productSetId : null)
  )

export const isProductToggled = (
  toggleds: $ReadOnlyArray<ProductOrSet>,
  product: ProductOrSet
): boolean => {
  // not set
  if (!toggleds.length) {
    return false
  }
  return getToggledProductIndex(toggleds, product) > -1
}

const getRevealedProductIndex = (
  revealeds: $ReadOnlyArray<ProductOrSet>,
  product: ProductOrSet
): number =>
  findIndex(
    revealeds,
    (item) => item.isSet == product.isSet && item.id == product.id
  )

export const isProductRevealed = (
  revealeds: $ReadOnlyArray<ProductOrSet>,
  product: ProductOrSet
): boolean => {
  // not set
  if (!revealeds.length) {
    return false
  }
  return getRevealedProductIndex(revealeds, product) > -1
}

// TODO and note: there is optimization to get here if needed
// The root data we receive are products grouped by category
// So in order to retrieve or find individual products, we sometimes
// need to traverse the nested list of first get the flat list
// of all products to then search for a specific ID
// Memoization can help here, but for now we just traverse
// and find

// selectors
const listProductsByCategory = (state: State) =>
  state.products.entities.products

export const getProductCategories = createSelector(
  [listProductsByCategory],
  (products) =>
    products.map(({id, name, products}) => ({
      id,
      name,
      productCount: products.filter((p) => !p.isHidden).length,
    }))
)

export const getProductsByCategory = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>,
  categoryId: number
): $ReadOnlyArray<ProductOrSet> => {
  const productCategoryIdx = productsByCategory
    ? productsByCategory.findIndex((pc) => pc.id == categoryId)
    : -1
  if (productCategoryIdx == -1) {
    return ([]: Product[])
  }
  return productsByCategory[productCategoryIdx].products
}

export const getActiveProducts = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): $ReadOnlyArray<ProductsByCategory> =>
  productsByCategory.map((pc) => ({
    ...pc,
    products: pc.products.filter((p) => !p.isHidden),
  }))

export const getProductCategory = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>,
  categoryId: number
): ?ProductCategory => {
  const idx = productsByCategory
    ? productsByCategory.findIndex((pc) => pc.id == categoryId)
    : -1
  if (idx !== -1) {
    const category = productsByCategory[idx]
    return {
      id: category.id,
      name: category.name,
      productCount: 0,
    }
  }
  return null
}

export const isOptional = (
  optionals: $ReadOnlyArray<BabyListOptionalItem>,
  product: ProductOrSet
): boolean =>
  optionals.filter((item: BabyListOptionalItem) => {
    if (product.isSet) {
      return item.productSetId == product.id
    }
    return item.productId == product.id
  }).length > 0

export const getOptionalByProduct = (
  optionals: $ReadOnlyArray<BabyListOptionalItem>,
  product: ProductOrSet
): ?BabyListOptionalItem => {
  const idx = optionals.findIndex((item: BabyListOptionalItem) => {
    if (product.isSet) {
      return item.productSetId == product.id
    }
    return item.productId == product.id
  })
  if (idx != -1) {
    return optionals[idx]
  }
  return null
}

export const getProductsByOptionals = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>,
  optionals: $ReadOnlyArray<BabyListOptionalItem>
): $ReadOnlyArray<ProductOrSet> => {
  const allProducts = getAllProductsWithChildren(productsByCategory)
  return optionals.reduce(
    (
      acc: $ReadOnlyArray<ProductOrSet>,
      optional: BabyListOptionalItem
    ): $ReadOnlyArray<ProductOrSet> => {
      const optionalProduct = getProductByOptional(optional, allProducts)
      return optionalProduct ? [...acc, optionalProduct] : acc
    },
    []
  )
}

export const getAllProducts = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): $ReadOnlyArray<ProductOrSet> => {
  return flatMap(productsByCategory, (pc) => pc.products)
}

export const getAllProductsWithChildren = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): $ReadOnlyArray<ProductOrSet> => {
  return uniqBy(
    flatMap(
      flatMap(productsByCategory, (pc) => pc.products),
      (p: ProductOrSet) => {
        if (p.isSet) {
          return [...p.products.map((psp) => psp.product), p]
        }
        return [p]
      }
    ),
    (p) => {
      return p.isSet ? `ps${p.id}` : `p${p.id}`
    }
  )
}

const getProductByOptional = (
  optional: BabyListOptionalItem,
  products: $ReadOnlyArray<ProductOrSet>
): ?ProductOrSet => {
  const idx = products.findIndex((p) => {
    if (optional.productId && !p.isSet && p.id === optional.productId) {
      return true
    }
    if (optional.productSetId && p.isSet && p.id === optional.productSetId) {
      return true
    }
    return false
  })
  return idx != -1 ? products[idx] : null
}

export const getProductsForLater = (
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): $ReadOnlyArray<ProductOrSet> => {
  return getAllProducts(productsByCategory).filter((p) => !p.isFirstPickup)
}

export const getProduct = (
  id: number,
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): ?Product => {
  const products = getAllProducts(productsByCategory)
  const idx = products.findIndex((p) => p.id === id && p.isSet == false)
  // allthough we ignore the non sets in the predicate above, flow
  // still needs some convincing that it is a product...
  return idx != -1 && !products[idx].isSet ? products[idx] : null
}

export const getProductSet = (
  id: number,
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): ?ProductSet => {
  const products = getAllProducts(productsByCategory)
  const idx = products.findIndex((p) => p.id === id && p.isSet == true)
  return idx != -1 && products[idx].isSet ? products[idx] : null
}

export const getProductByBabyListItem = (
  babyListItem: BabyListItem,
  productsByCategory: $ReadOnlyArray<ProductsByCategory>
): ?ProductOrSet => {
  // we know that  if the productSetId is null, the productId will be set
  if (babyListItem.productSetId) {
    return getProductSet(babyListItem.productSetId, productsByCategory)
  }
  return getProduct(babyListItem.productId, productsByCategory)
}

// export const isInSet = (product: ProductOrSet, productSet: ProductSet) =>
//   productSet.products.findIndex(p => p.id == product.id) !== -1

export const fuzzProducts = (
  q: string,
  products: $ReadOnlyArray<ProductOrSet>
): $ReadOnlyArray<ProductOrSet> =>
  fuzzysort.go(
    q,
    products.filter((p) => p.categoryId !== null),
    {keys: ["name"]}
  )

export default products
